'use client';

import React, { useState, useMemo } from 'react';
import AppLayout from '@/components/AppLayout';
import { Search, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle, Sliders, Clock, CheckCircle, XCircle, Loader2, Eye, ChevronDown, ChevronUp, Package } from 'lucide-react';

type OrderStatus = 'Pending' | 'Processing' | 'Fulfilled' | 'Rejected';
type ReplenishType = 'Fresh' | 'Partial' | 'Custom';

interface KitLineItem {
  kitName: string;
  serialNo?: string;
  quantity: number;
  reason?: string;
}

interface ReplenishmentOrder {
  id: string;
  requestDate: string;
  type: ReplenishType;
  kits: KitLineItem[];
  totalQuantity: number;
  status: OrderStatus;
  referenceNo: string;
}

const MOCK_ORDERS: ReplenishmentOrder[] = [
  {
    id: 'ORD-001', requestDate: '2026-06-28', type: 'Fresh', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/001',
    totalQuantity: 50,
    kits: [{ kitName: 'BLOOD GROUPING KIT', quantity: 50 }],
  },
  {
    id: 'ORD-002', requestDate: '2026-06-25', type: 'Partial', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/002',
    totalQuantity: 55,
    kits: [
      { kitName: 'MALARIA TEST KIT', serialNo: 'BHISHM/AIIMS/MED/PTN/01', quantity: 1, reason: 'Expired' },
      { kitName: 'DENGUE TEST KIT', serialNo: 'BHISHM/AIIMS/LAB/PTN/03', quantity: 1, reason: 'Expired' },
      { kitName: 'TYPHOID TEST KIT', serialNo: 'BHISHM/AIIMS/TECH/PTN/07', quantity: 1, reason: 'Kits Short' },
      { kitName: 'HIV TEST KIT', serialNo: 'BHISHM/AIIMS/SURG/PTN/02', quantity: 1, reason: 'Expired' },
      { kitName: 'HEPATITIS B KIT', serialNo: 'BHISHM/AIIMS/ICU/PTN/05', quantity: 1, reason: 'Expiring in 3M' },
    ],
  },
  {
    id: 'ORD-003', requestDate: '2026-06-22', type: 'Custom', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/003',
    totalQuantity: 60,
    kits: [
      { kitName: 'DENGUE TEST KIT', quantity: 35 },
      { kitName: 'TROPONIN TEST KIT', quantity: 25 },
    ],
  },
  {
    id: 'ORD-004', requestDate: '2026-06-20', type: 'Partial', status: 'Rejected', referenceNo: 'REP/AIIMS/2026/004',
    totalQuantity: 15,
    kits: [
      { kitName: 'HIV TEST KIT', serialNo: 'BHISHM/AIIMS/MED/PTN/04', quantity: 1, reason: 'Expired' },
      { kitName: 'CHOLERA TEST KIT', serialNo: 'BHISHM/AIIMS/LAB/PTN/08', quantity: 1, reason: 'Expired' },
      { kitName: 'LEPTOSPIROSIS KIT', serialNo: 'BHISHM/AIIMS/TECH/PTN/11', quantity: 1, reason: 'Kits Short' },
    ],
  },
  {
    id: 'ORD-005', requestDate: '2026-06-18', type: 'Fresh', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/005',
    totalQuantity: 100,
    kits: [{ kitName: 'PREGNANCY TEST KIT', quantity: 100 }],
  },
  {
    id: 'ORD-006', requestDate: '2026-06-15', type: 'Custom', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/006',
    totalQuantity: 25,
    kits: [{ kitName: 'TROPONIN TEST KIT', quantity: 25 }],
  },
  {
    id: 'ORD-007', requestDate: '2026-06-12', type: 'Partial', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/007',
    totalQuantity: 40,
    kits: [
      { kitName: 'TYPHOID TEST KIT', serialNo: 'BHISHM/AIIMS/MED/PTN/06', quantity: 1, reason: 'Expired' },
      { kitName: 'URINE STRIP KIT', serialNo: 'BHISHM/AIIMS/LAB/PTN/09', quantity: 1, reason: 'Expired' },
      { kitName: 'STOOL TEST KIT', serialNo: 'BHISHM/AIIMS/SURG/PTN/12', quantity: 1, reason: 'Expiring in 6M' },
      { kitName: 'GLUCOSE TEST KIT', serialNo: 'BHISHM/AIIMS/ICU/PTN/15', quantity: 1, reason: 'Kits Short' },
    ],
  },
  {
    id: 'ORD-008', requestDate: '2026-06-10', type: 'Fresh', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/008',
    totalQuantity: 60,
    kits: [{ kitName: 'HEPATITIS B KIT', quantity: 60 }],
  },
  {
    id: 'ORD-009', requestDate: '2026-07-01', type: 'Fresh', status: 'Pending', referenceNo: 'REP/AIIMS/2026/009',
    totalQuantity: 80,
    kits: [{ kitName: 'WOUND CARE KIT', quantity: 80 }],
  },
  {
    id: 'ORD-010', requestDate: '2026-07-01', type: 'Partial', status: 'Processing', referenceNo: 'REP/AIIMS/2026/010',
    totalQuantity: 30,
    kits: [
      { kitName: 'TRAUMA CARE KIT', serialNo: 'BHISHM/AIIMS/MED/PTN/10', quantity: 1, reason: 'Expired' },
      { kitName: 'BURN CARE KIT', serialNo: 'BHISHM/AIIMS/LAB/PTN/14', quantity: 1, reason: 'Expired' },
      { kitName: 'SNAKE BITE KIT', serialNo: 'BHISHM/AIIMS/TECH/PTN/16', quantity: 1, reason: 'Expiring in 3M' },
      { kitName: 'ANTIDOTE KIT', serialNo: 'BHISHM/AIIMS/SURG/PTN/18', quantity: 1, reason: 'Kits Short' },
      { kitName: 'RESUSCITATION KIT', serialNo: 'BHISHM/AIIMS/ICU/PTN/20', quantity: 1, reason: 'Expired' },
    ],
  },
  {
    id: 'ORD-011', requestDate: '2026-06-30', type: 'Custom', status: 'Processing', referenceNo: 'REP/AIIMS/2026/011',
    totalQuantity: 18,
    kits: [{ kitName: 'BURN CARE KIT', quantity: 18 }],
  },
  {
    id: 'ORD-012', requestDate: '2026-06-29', type: 'Fresh', status: 'Pending', referenceNo: 'REP/AIIMS/2026/012',
    totalQuantity: 45,
    kits: [{ kitName: 'SURGICAL DRESSING KIT', quantity: 45 }],
  },
  {
    id: 'ORD-013', requestDate: '2026-06-08', type: 'Partial', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/013',
    totalQuantity: 22,
    kits: [
      { kitName: 'CHOLERA TEST KIT', serialNo: 'BHISHM/AIIMS/MED/PTN/22', quantity: 1, reason: 'Expired' },
      { kitName: 'SCRUB TYPHUS KIT', serialNo: 'BHISHM/AIIMS/LAB/PTN/25', quantity: 1, reason: 'Expired' },
    ],
  },
  {
    id: 'ORD-014', requestDate: '2026-06-05', type: 'Custom', status: 'Fulfilled', referenceNo: 'REP/AIIMS/2026/014',
    totalQuantity: 12,
    kits: [{ kitName: 'SNAKE BITE KIT', quantity: 12 }],
  },
  {
    id: 'ORD-015', requestDate: '2026-06-03', type: 'Fresh', status: 'Rejected', referenceNo: 'REP/AIIMS/2026/015',
    totalQuantity: 10,
    kits: [{ kitName: 'RESUSCITATION KIT', quantity: 10 }],
  },
];

const PAGE_SIZE = 10;

const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  Pending: { label: 'Pending', color: '#b45309', bg: 'rgba(180,83,9,0.08)', border: 'rgba(180,83,9,0.2)', icon: <Clock size={11} /> },
  Processing: { label: 'Processing', color: '#0369a1', bg: 'rgba(3,105,161,0.08)', border: 'rgba(3,105,161,0.2)', icon: <Loader2 size={11} /> },
  Fulfilled: { label: 'Fulfilled', color: '#16a34a', bg: 'rgba(22,163,74,0.08)', border: 'rgba(22,163,74,0.2)', icon: <CheckCircle size={11} /> },
  Rejected: { label: 'Rejected', color: '#dc2626', bg: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.2)', icon: <XCircle size={11} /> },
};

const TYPE_CONFIG: Record<ReplenishType, { color: string; bg: string; icon: React.ReactNode }> = {
  Fresh: { color: '#0369a1', bg: 'rgba(3,105,161,0.08)', icon: <RefreshCw size={11} /> },
  Partial: { color: '#b45309', bg: 'rgba(180,83,9,0.08)', icon: <AlertTriangle size={11} /> },
  Custom: { color: '#7c3aed', bg: 'rgba(124,58,237,0.08)', icon: <Sliders size={11} /> },
};

const REASON_COLOR: Record<string, { color: string; bg: string }> = {
  'Expired': { color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  'Kits Short': { color: '#ea580c', bg: 'rgba(234,88,12,0.08)' },
  'Expiring in 3M': { color: '#b45309', bg: 'rgba(180,83,9,0.08)' },
  'Expiring in 6M': { color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
};

function formatDate(iso: string) {
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

type FilterStatus = 'All' | OrderStatus;
type FilterType = 'All' | ReplenishType;

function KitListExpanded({ kits, type }: { kits: KitLineItem[]; type: ReplenishType }) {
  return (
    <div
      className="mt-2 rounded-xl overflow-hidden"
      style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}
    >
      <div
        className="px-4 py-2 flex items-center gap-2"
        style={{ borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' }}
      >
        <Package size={12} style={{ color: '#64748b' }} />
        <span className="text-[11px] font-600" style={{ color: '#64748b' }}>
          {kits.length} Kit{kits.length !== 1 ? 's' : ''} in this order
        </span>
      </div>
      <div className="divide-y" style={{ borderColor: '#e2e8f0' }}>
        {kits.map((kit, idx) => (
          <div key={idx} className="px-4 py-2.5 flex items-center gap-3">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: TYPE_CONFIG[type].bg }}
            >
              <Package size={11} style={{ color: TYPE_CONFIG[type].color }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 truncate" style={{ color: '#0f2744' }}>{kit.kitName}</p>
              {kit.serialNo && (
                <p className="text-[10px] mt-0.5" style={{ color: '#94a3b8' }}>Serial: {kit.serialNo}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {kit.reason && (
                <span
                  className="text-[10px] font-500 px-2 py-0.5 rounded-full"
                  style={{
                    background: REASON_COLOR[kit.reason]?.bg ?? 'rgba(100,116,139,0.08)',
                    color: REASON_COLOR[kit.reason]?.color ?? '#64748b',
                  }}
                >
                  {kit.reason}
                </span>
              )}
              <span className="text-[11px] font-600" style={{ color: '#475569' }}>
                Qty: {kit.quantity}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ReplenishmentOrdersPage() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('All');
  const [filterType, setFilterType] = useState<FilterType>('All');
  const [page, setPage] = useState(1);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  function toggleExpand(id: string) {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return MOCK_ORDERS.filter(o => {
      const matchSearch = !q
        || o.referenceNo.toLowerCase().includes(q)
        || o.id.toLowerCase().includes(q)
        || o.kits.some(k => k.kitName.toLowerCase().includes(q));
      const matchStatus = filterStatus === 'All' || o.status === filterStatus;
      const matchType = filterType === 'All' || o.type === filterType;
      return matchSearch && matchStatus && matchType;
    });
  }, [search, filterStatus, filterType]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleFilterChange() { setPage(1); }

  const counts = useMemo(() => ({
    total: MOCK_ORDERS.length,
    pending: MOCK_ORDERS.filter(o => o.status === 'Pending').length,
    processing: MOCK_ORDERS.filter(o => o.status === 'Processing').length,
    fulfilled: MOCK_ORDERS.filter(o => o.status === 'Fulfilled').length,
    rejected: MOCK_ORDERS.filter(o => o.status === 'Rejected').length,
  }), []);

  const STAT_CARDS = [
    { label: 'Total Orders', value: counts.total, color: '#0369a1', bg: 'rgba(3,105,161,0.07)' },
    { label: 'Pending', value: counts.pending, color: '#b45309', bg: 'rgba(180,83,9,0.07)' },
    { label: 'Processing', value: counts.processing, color: '#0369a1', bg: 'rgba(3,105,161,0.07)' },
    { label: 'Fulfilled', value: counts.fulfilled, color: '#16a34a', bg: 'rgba(22,163,74,0.07)' },
    { label: 'Rejected', value: counts.rejected, color: '#dc2626', bg: 'rgba(220,38,38,0.07)' },
  ];

  return (
    <AppLayout activePath="/replenishment-orders">
      <div className="px-4 lg:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-lg font-700" style={{ color: '#0f2744' }}>Replenishment Orders</h1>
          <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
            Track all past and pending replenishment requests — AIIMS PATNA
          </p>
        </div>

        {/* Summary stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {STAT_CARDS.map(s => (
            <div
              key={s.label}
              className="rounded-xl px-4 py-3 flex flex-col gap-0.5"
              style={{ background: s.bg, border: `1px solid ${s.color}22` }}
            >
              <span className="text-[11px] font-500" style={{ color: '#64748b' }}>{s.label}</span>
              <span className="text-xl font-700" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>

        {/* Filters row */}
        <div className="flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search kit name, ref no…"
              value={search}
              onChange={e => { setSearch(e.target.value); handleFilterChange(); }}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg outline-none"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1a2332' }}
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value as FilterStatus); handleFilterChange(); }}
            className="text-xs px-3 py-2 rounded-lg outline-none cursor-pointer"
            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1a2332' }}
          >
            <option value="All">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
            <option value="Fulfilled">Fulfilled</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select
            value={filterType}
            onChange={e => { setFilterType(e.target.value as FilterType); handleFilterChange(); }}
            className="text-xs px-3 py-2 rounded-lg outline-none cursor-pointer"
            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#1a2332' }}
          >
            <option value="All">All Types</option>
            <option value="Fresh">Fresh</option>
            <option value="Partial">Partial</option>
            <option value="Custom">Custom</option>
          </select>
          <span className="text-xs self-center ml-auto" style={{ color: '#94a3b8' }}>
            {filtered.length} order{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th className="text-left px-4 py-3 font-600" style={{ color: '#64748b' }}>Ref No.</th>
                  <th className="text-left px-4 py-3 font-600" style={{ color: '#64748b' }}>Request Date</th>
                  <th className="text-left px-4 py-3 font-600" style={{ color: '#64748b' }}>Kits</th>
                  <th className="text-left px-4 py-3 font-600" style={{ color: '#64748b' }}>Type</th>
                  <th className="text-center px-4 py-3 font-600" style={{ color: '#64748b' }}>Total Qty</th>
                  <th className="text-left px-4 py-3 font-600" style={{ color: '#64748b' }}>Status</th>
                  <th className="text-center px-4 py-3 font-600" style={{ color: '#64748b' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-xs" style={{ color: '#94a3b8' }}>
                      No orders found matching your filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((order, idx) => {
                    const statusCfg = STATUS_CONFIG[order.status];
                    const typeCfg = TYPE_CONFIG[order.type];
                    const isExpanded = expandedOrders.has(order.id);
                    return (
                      <React.Fragment key={order.id}>
                        <tr
                          style={{
                            borderBottom: !isExpanded && idx < paginated.length - 1 ? '1px solid #f1f5f9' : 'none',
                            background: idx % 2 === 0 ? '#ffffff' : '#fafbfc',
                          }}
                        >
                          <td className="px-4 py-3 font-500" style={{ color: '#0369a1' }}>{order.referenceNo}</td>
                          <td className="px-4 py-3" style={{ color: '#475569' }}>{formatDate(order.requestDate)}</td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-600" style={{ color: '#1a2332' }}>
                                {order.kits.length === 1
                                  ? order.kits[0].kitName
                                  : `${order.kits.length} kits`}
                              </p>
                              {order.kits.length > 1 && (
                                <p className="text-[10px] mt-0.5 truncate max-w-[180px]" style={{ color: '#94a3b8' }}>
                                  {order.kits.slice(0, 2).map(k => k.kitName).join(', ')}{order.kits.length > 2 ? ` +${order.kits.length - 2} more` : ''}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-500"
                              style={{ background: typeCfg.bg, color: typeCfg.color }}
                            >
                              {typeCfg.icon}
                              {order.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center font-600" style={{ color: '#1a2332' }}>{order.totalQuantity}</td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-500"
                              style={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}
                            >
                              {statusCfg.icon}
                              {statusCfg.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => toggleExpand(order.id)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-600 transition-all"
                              style={isExpanded
                                ? { background: 'rgba(3,105,161,0.12)', color: '#0369a1', border: '1px solid rgba(3,105,161,0.25)' }
                                : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }
                              }
                            >
                              <Eye size={11} />
                              View
                              {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: idx % 2 === 0 ? '#ffffff' : '#fafbfc', borderBottom: idx < paginated.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <td colSpan={7} className="px-4 pb-4">
                              <KitListExpanded kits={order.kits} type={order.type} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y" style={{ borderColor: '#f1f5f9' }}>
            {paginated.length === 0 ? (
              <div className="text-center py-10 text-xs" style={{ color: '#94a3b8' }}>No orders found.</div>
            ) : (
              paginated.map(order => {
                const statusCfg = STATUS_CONFIG[order.status];
                const typeCfg = TYPE_CONFIG[order.type];
                const isExpanded = expandedOrders.has(order.id);
                return (
                  <div key={order.id} className="px-4 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-600" style={{ color: '#1a2332' }}>
                          {order.kits.length === 1 ? order.kits[0].kitName : `${order.kits.length} kits`}
                        </p>
                        <p className="text-[10px] mt-0.5" style={{ color: '#0369a1' }}>{order.referenceNo}</p>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-500 flex-shrink-0"
                        style={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}` }}
                      >
                        {statusCfg.icon}
                        {statusCfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px]" style={{ color: '#64748b' }}>{formatDate(order.requestDate)}</span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-500"
                        style={{ background: typeCfg.bg, color: typeCfg.color }}
                      >
                        {typeCfg.icon}
                        {order.type}
                      </span>
                      <span className="text-[10px] font-600" style={{ color: '#1a2332' }}>Total Qty: {order.totalQuantity}</span>
                    </div>
                    <button
                      onClick={() => toggleExpand(order.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-600 transition-all"
                      style={isExpanded
                        ? { background: 'rgba(3,105,161,0.12)', color: '#0369a1', border: '1px solid rgba(3,105,161,0.25)' }
                        : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }
                      }
                    >
                      <Eye size={11} />
                      {isExpanded ? 'Hide Kits' : 'View Kits'}
                      {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                    </button>
                    {isExpanded && (
                      <KitListExpanded kits={order.kits} type={order.type} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-xs" style={{ color: '#94a3b8' }}>
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
                style={{ border: '1px solid #e2e8f0', color: '#64748b' }}
              >
                <ChevronLeft size={13} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-1 text-xs" style={{ color: '#94a3b8' }}>…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className="w-7 h-7 rounded-lg text-xs font-500 transition-colors"
                      style={{
                        background: currentPage === p ? '#0369a1' : 'transparent',
                        color: currentPage === p ? '#ffffff' : '#64748b',
                        border: currentPage === p ? 'none' : '1px solid #e2e8f0',
                      }}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
                style={{ border: '1px solid #e2e8f0', color: '#64748b' }}
              >
                <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}
